const INTENDED_URL_KEY = 'intendedUrl';

/**
 * Stores the current URL (pathname + search) so the user can be
 * redirected there after completing the login flow.
 * Only stores /home/* paths to avoid redirect loops.
 *
 * Uses localStorage (not sessionStorage) because the post-logout flow
 * triggers a full-page reload via `window.location.assign('/')`, which
 * wipes sessionStorage. `endSession()` explicitly preserves this key
 * across its `localStorage.clear()` so the value also survives the
 * storage wipe that runs just before the redirect.
 */
export const storeIntendedUrl = (): void => {
  const { pathname, search } = window.location;
  if (pathname.startsWith('/home')) {
    const url = pathname + search;
    localStorage.setItem(INTENDED_URL_KEY, url);
  }
};

/**
 * Peeks at the stored intended URL without removing it.
 * Used by org selection to check for entityUUID before the URL is consumed.
 */
export const peekIntendedUrl = (): {
  pathname: string;
  queryParams: Record<string, string>;
} | null => {
  const url = localStorage.getItem(INTENDED_URL_KEY);
  if (url) {
    const qIndex = url.indexOf('?');
    if (qIndex >= 0) {
      const pathname = url.substring(0, qIndex);
      const queryParams: Record<string, string> = {};
      new URLSearchParams(url.substring(qIndex)).forEach((v, k) => {
        queryParams[k] = v;
      });
      return { pathname, queryParams };
    }
    return { pathname: url, queryParams: {} };
  }
  return null;
};

/**
 * Retrieves and clears the stored intended URL.
 * Returns { pathname, queryParams } if a URL was stored, or null.
 * Split into parts so callers can pass them to navigateTo(pathname, queryParams)
 * without query strings being percent-encoded in the pathname.
 */
export const consumeIntendedUrl = (): {
  pathname: string;
  queryParams: Record<string, string>;
} | null => {
  const url = localStorage.getItem(INTENDED_URL_KEY);
  if (url) {
    localStorage.removeItem(INTENDED_URL_KEY);
    const qIndex = url.indexOf('?');
    if (qIndex >= 0) {
      const pathname = url.substring(0, qIndex);
      const queryParams: Record<string, string> = {};
      new URLSearchParams(url.substring(qIndex)).forEach((v, k) => {
        queryParams[k] = v;
      });
      return { pathname, queryParams };
    }
    return { pathname: url, queryParams: {} };
  }
  return null;
};

/**
 * The target of the last stored deep link that was REFUSED (DV-3012).
 *
 * Paired with `intendedUrl`, and only meaningful next to it. BC-2 keeps the
 * intended URL after a denial so `/error`'s "Switch Organization" CTA can
 * still land on it once the user picks a reachable account — but the stored
 * URL is then read by `resolveEntityScope` on EVERY subsequent bootstrap,
 * including the `/` that "Go to Home" assigns. Without a record that this
 * exact target was already refused, that read denies again, reassigns
 * `/error`, and the session is bricked: signing out does not help either,
 * because `intendedUrl` is deliberately preserved across `endSession`.
 *
 * So the negative is stored alongside the positive, and preserved alongside
 * it (see SESSION_PRESERVED_KEYS in auth-token.ts).
 */
const DENIED_TARGET_KEY = 'deniedEntityTarget';

/**
 * Record that `target` — read from the stored intended URL, never from the
 * current URL — was refused. A target on the current URL needs no marker: it
 * is re-supplied by the user each time, so re-resolving it is correct, and
 * nothing re-reads it on its own.
 */
export const markIntendedTargetDenied = (target: string): void => {
  localStorage.setItem(DENIED_TARGET_KEY, target);
};

/** True when `target` is the one the stored intended URL was refused for. */
export const isIntendedTargetDenied = (target: string): boolean =>
  localStorage.getItem(DENIED_TARGET_KEY) === target;

/**
 * Retire a refused deep link: the URL and its marker go together.
 *
 * Called the first time the refused target would be resolved again, which is
 * necessarily AFTER the user has left `/error` (that route is `entity: 'none'`
 * and resolves nothing), so BC-2's CTA is unaffected — that path goes picker →
 * `switchEntity('land-intended')`, which consumes the URL itself. Dropping
 * the URL here, rather than merely skipping it, is what stops a later
 * unrelated `'land-intended'` switch (the single-org auto-select, say) from
 * landing the user back on the poisoned link.
 */
export const retireDeniedIntendedUrl = (): void => {
  localStorage.removeItem(DENIED_TARGET_KEY);
  localStorage.removeItem(INTENDED_URL_KEY);
};

/**
 * Drop the marker alone, keeping any stored intended URL.
 *
 * A completed entity switch changes which entities the session can reach, so
 * an earlier refusal is no longer authoritative. `switchEntity` calls this;
 * it is the one place every switch surface funnels through.
 */
export const clearDeniedIntendedTarget = (): void => {
  localStorage.removeItem(DENIED_TARGET_KEY);
};
